import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { api } from "@/services/api";
import { CountdownTimer } from "@/components/CountdownTimer";
import { WeatherWidget } from "@/components/WeatherWidget";
import { NewsCard } from "@/components/NewsCard";
import { Footer } from "@/components/Footer";
import { ErrorState, LoadingState } from "@/components/States";
import { LaunchInfoGrid } from "@/components/launch/LaunchInfoGrid";
import { MissionDescription } from "@/components/launch/MissionDescription";
import { LaunchActions } from "@/components/launch/LaunchActions";
import { LaunchHeroImage } from "@/components/launch/LaunchHeroImage";

export default function LaunchDetailPage({ zipCode }) {
  const { id } = useParams();
  const [launch, setLaunch] = useState(null);
  const [weather, setWeather] = useState(null);
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [launchRes, weatherRes, newsRes] = await Promise.all([
        api.getLaunchDetail(id),
        api.getWeather(zipCode),
        api.getNews(3),
      ]);
      setLaunch(launchRes.launch);
      setWeather(weatherRes);
      setNews(newsRes.news);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, zipCode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 md:px-8 py-8">
          <LoadingState message="Loading launch details..." />
        </div>
      </div>
    );
  }

  if (error || !launch) {
    return (
      <div className="min-h-screen">
        <div className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 md:px-8 py-8">
          <Link to="/launches" className="inline-flex items-center gap-2 text-cosmic-blue mb-6">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back to Launches
          </Link>
          <ErrorState message={error || "Launch not found"} onRetry={fetchData} />
        </div>
      </div>
    );
  }

  const launchDate = launch.net ? new Date(launch.net) : null;
  const formattedDate = launchDate
    ? format(launchDate, "EEEE, MMMM d, yyyy 'at' h:mm a zzz")
    : "Date TBD";

  return (
    <div className="min-h-screen" data-testid="launch-detail-page">
      <div className="hero-gradient fixed inset-0 pointer-events-none" />
      
      <div className="relative max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 md:px-8 py-8">
        <div className="mb-4">
          <span className="text-sm md:text-base font-heading font-bold uppercase tracking-[0.25em] text-cosmic-blue">
            The PMA Project
          </span>
        </div>
        
        <Link
          to="/launches"
          className="inline-flex items-center gap-2 text-cosmic-blue hover:text-cosmic-blue-glow mb-6"
          style={{ transition: "color 0.2s ease" }}
          data-testid="back-to-launches"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="font-heading font-semibold uppercase tracking-wider text-sm">Back to Launches</span>
        </Link>

        <div className="space-y-6">
          <div className="glass-card overflow-hidden">
            <LaunchHeroImage launch={launch} />

            <div className="p-6 md:p-8">
              <div className="mb-4">
                <p className="text-sm text-cosmic-blue font-heading uppercase tracking-wider mb-1">
                  {launch.provider}
                </p>
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-white">
                  {launch.name}
                </h1>
              </div>

              <LaunchInfoGrid launch={launch} formattedDate={formattedDate} />

              {launchDate && (
                <div className="mb-6 py-6 border-y border-white/10">
                  <p className="text-xs text-slate-500 uppercase tracking-widest mb-4 text-center font-heading">
                    Countdown to Launch
                  </p>
                  <CountdownTimer targetDate={launch.net} />
                </div>
              )}

              <MissionDescription description={launch.mission_description} />
              <LaunchActions launch={launch} />
            </div>
          </div>

          {weather && (
            <section>
              <h2 className="section-title mb-4">Launch Viewing Conditions</h2>
              <WeatherWidget weather={weather} />
            </section>
          )}

          {news && news.length > 0 && (
            <section>
              <h2 className="section-title mb-4">Related News</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {news.map((article) => (
                  <NewsCard key={article.id} article={article} />
                ))}
              </div>
            </section>
          )}
        </div>

        <Footer />
      </div>
    </div>
  );
}
